<template>
  <div data-testid="viewer-template-config-form">
    <FormKit @submit="onSubmit" type="form" :actions="false" :preserve-errors="true" v-model="model" id="templateForm">
      <div
        class="fixed z-[99] overflow-y-auto top-5 right-5 bottom-5 p-6 pb-[6.875rem] shadow-lg bg-white dark:bg-zinc-800 w-full sm:w-80 border border-slate-50 dark:border-zinc-700 rounded-10"
      >
        <div class="flex flex-col gap-4">
          <h3 class="text-2xl font-semibold dark:text-zinc-300">Apply template</h3>

          <!-- Template Basic Info -->
          <FormKit
            type="text"
            validation="required:trim|length:1,255"
            label="Template name"
            name="templateName"
            placeholder="Template name"
            :disabled="!props.isCustom"
          />

          <FormKit v-if="props.isCustom" type="textarea" label="Template description" name="templateDescription" placeholder="" />

          <FormKit type="text" validation="required:trim|length:1,255" label="Default title" name="defaultTitle" placeholder="Default title" />

          <FormKit type="textarea" label="Default description" name="defaultDescription" placeholder="Default description" />

          <HorizontalDivider />

          <!-- Interaction Settings -->
          <FormKit type="toggle" label="Anyone can comment" name="commentEnabled" />
          <FormKit type="toggle" label="Allow reactions" name="reactionEnabled" />
          <FormKit type="toggle" label="Add button" name="buttonEnabled" />

          <div v-show="model.buttonEnabled" class="flex flex-col gap-4">
            <FormKit type="text" validation="required:trim|length:1,255" label="Button text" name="buttonText" placeholder="Button text" />
            <FormKit type="url" validation="required:trim|url" label="Button URL" name="buttonLink" placeholder="Button link" />
          </div>

          <HorizontalDivider />

          <!-- Security Settings -->
          <Accordion summary="Custom security settings" :open="hasCustomSecurity">
            <div class="my-3 flex flex-col gap-2">
              <FormKit
                label="Who can access this link?"
                type="radio"
                name="securitySetting"
                :options="[
                  { label: 'Anyone with the link can access', value: 'un' },
                  { label: 'Anyone in your organization', value: 'og' },
                ]"
              />
              <FormKit type="toggle" label="Automatically expire" name="expireDaysEnabled" />
              <FormKit v-if="model.expireDaysEnabled" type="number" label="Days until expiration" name="expireDays" :min="1" :max="365" />
            </div>
          </Accordion>

          <!-- Collections -->
          <Accordion summary="Select collections" :open="isCollectionsAccordionOpen">
            <div class="my-3 flex flex-col gap-2">
              <FormKit
                v-if="collectionOptions.length > 0"
                :label="collectionsLabel"
                type="checkbox"
                :model-value="selectedCollectionIds"
                :options="collectionOptions"
                @update:model-value="
                  (ids: string[]) => {
                    selectedCollectionIds = ids;
                  }
                "
              />
              <Button type="link" variant="blue" @click="handleShowAllCollections"> Show all collections ({{ collectionsList.length }}) </Button>
            </div>
          </Accordion>

          <!-- Branding -->
          <Button :href="brandingUrl" target="_blank" type="solid" variant="gray" icon="general.stars">Customize branding</Button>

          <!-- Organization Settings -->
          <FormKit v-if="props.isCustom" type="toggle" label="Share template with my workspace" name="belongsToOrg" />
        </div>
      </div>

      <div
        class="fixed z-[100] bottom-5 right-5 w-full sm:w-80 p-6 flex gap-4 bg-white dark:bg-zinc-800 rounded-b-10 border border-slate-100 dark:border-zinc-700"
      >
        <Button :submit="true" data-testid="viewer-template-save" type="solid" variant="blue" class="flex-1"> Save & apply template </Button>
        <Button @click.prevent="onCancel" data-testid="viewer-template-cancel" type="link" variant="black"> Cancel </Button>
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { Button, HorizontalDivider, useModal } from '@/storybook';
import Accordion from '@/storybook/accordion/Accordion.vue';
import { ref, watch, computed, onMounted, onUnmounted, type Ref } from 'vue';
import type { TemplateFormData } from '@/modules/viewer/store/useViewerTemplateStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useCallToActionStore } from '@/modules/viewer/store/useCallToActionStore';
import { useCollectionsStore, type CollectionItemInterface } from '@/store/useCollectionsStore';
import { storeToRefs } from 'pinia';
import CollectionPicker from '@/components/CollectionPicker.vue';
import { useTemplateCollectionSelection } from '@/modules/viewer/composables/useTemplateCollectionSelection';

interface Props {
  formData: TemplateFormData;
  isCustom: boolean;
  onSubmit: (formData: TemplateFormData) => Promise<{ success: boolean; template?: any } | void>;
}

interface Emits {
  (e: 'close'): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const organizationStore = useOrganizationStore();
const ctaStore = useCallToActionStore();
const collectionsStore = useCollectionsStore();
const { list: collectionsList } = storeToRefs(collectionsStore);

const model: Ref<TemplateFormData> = ref({ ...props.formData });
const isCTABackedUp = ref(false);
const wasSaveSuccessful = ref(false);

// Collection selection
const { selectedCollectionIds, displayedCollections, collectionsLabel, toggleCollection, getCollectionIdsForSaving } = useTemplateCollectionSelection(
  props.formData.collections
);

// State for collections accordion - only auto-open on initial mount if there are selections
const isCollectionsAccordionOpen = ref(props.formData.collections && props.formData.collections.length > 0);

// Computed property to determine if security accordion should be open
const hasCustomSecurity = computed(() => {
  return model.value.securitySetting !== 'un' || model.value.expireDaysEnabled === true;
});

// Computed property for branding URL
const brandingUrl = computed(() => {
  const orgId = organizationStore.id;
  return orgId ? `/organizations/${orgId}/settings/customize` : '#';
});

// Computed property for collection options (for FormKit checkbox display)
const collectionOptions = computed(() => {
  return displayedCollections.value.map(c => ({
    label: c.name,
    value: c.id,
  }));
});

const { open: openCollectionPicker, close: closeCollectionPicker } = useModal(CollectionPicker, {
  dataTestid: 'collection-picker-modal',
  modalProps: {
    selectedId: selectedCollectionIds,
    onSelect: (collection: CollectionItemInterface) => {
      toggleCollection(collection.id);
    },
    onCancel: () => closeCollectionPicker(),
  },
});

const handleShowAllCollections = () => {
  openCollectionPicker();
};

const onSubmit = async () => {
  // Get collection IDs from composable (following SW-5847 pattern)
  const collectionIds = getCollectionIdsForSaving();

  try {
    const result = await props.onSubmit({
      ...model.value,
      collections: collectionIds,
    });

    // If submit was successful, mark as saved and close all modals
    if (result?.success) {
      wasSaveSuccessful.value = true; // Don't restore CTA on unmount
      closeCollectionPicker(); // Close collection picker if open
      emits('close'); // Close this modal
    }
  } catch (err) {
    // Error is already handled in the onSubmit handler (shows toast)
    console.error('Template save failed:', err);
  }
};

const onCancel = () => {
  closeCollectionPicker(); // Close collection picker if open
  emits('close');
};

// Watch for prop changes to update form data
watch(
  () => props.formData,
  newFormData => {
    model.value = { ...newFormData };
  },
  { immediate: false, deep: true }
);

// Watch button settings and update CTA store for preview (only after backup)
watch(
  () => ({
    displayAction: model.value.buttonEnabled,
    text: model.value.buttonText,
    link: model.value.buttonLink,
  }),
  ({ displayAction, text, link }) => {
    if (!isCTABackedUp.value || !ctaStore.cta || typeof text === 'undefined' || typeof link === 'undefined') return;

    ctaStore.setCta({
      ...ctaStore.cta,
      displayAction,
      text,
      link,
    });
  },
  { deep: true }
);

onMounted(async () => {
  // Load collections
  if (collectionsList.value.length === 0) {
    await collectionsStore.load();
  }

  // Backup current CTA state first
  ctaStore.backupCta();

  const { buttonEnabled: displayAction, buttonText: text, buttonLink: link } = model.value;

  // Initialize CTA store with template button settings
  if (ctaStore.cta && displayAction !== undefined && text !== undefined && link !== undefined) {
    ctaStore.setCta({
      ...ctaStore.cta,
      displayAction,
      text,
      link,
    });
  }

  // Enable watcher now that backup is done
  isCTABackedUp.value = true;
});

onUnmounted(() => {
  // Only restore CTA if save was not successful (user cancelled)
  if (!wasSaveSuccessful.value) {
    ctaStore.restoreCta();
  }
});
</script>

<style>
#templateForm > .formkit-messages {
  display: none;
}
</style>
